import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { parsePreferences, serializePreferences, userPreferenceService } from '@/api/domains/userPreference';
import { useAbortController } from '@/hooks/ui/useAbortController';
import type { RootState, AppDispatch } from '@/store';
import { setScreenPreferences, selectScreenPreferences } from '@/store/slices/userPreferencesSlice';

// Typed directly against react-redux (not re-exported via `@/hooks`) to avoid a module cycle:
// `usePaginatedList` (in `@/hooks/ui`, itself re-exported by the `@/hooks` barrel) calls this
// hook, so this file cannot depend on the `@/hooks` barrel without creating a circular import.
const useAppDispatch = useDispatch.withTypes<AppDispatch>();
const useAppSelector = useSelector.withTypes<RootState>();


export interface UseScreenPreferencesResult<T extends Record<string, unknown>> {
  preferences: T;
  updatePreferences: (patch: Partial<T>) => void;
  isLoaded: boolean;
}

/** Sentinel `screenCode` that disables persistence entirely (no fetch/cache/save-on-leave). */
const DISABLED_SCREEN_CODE = '';

/**
 * Generic, reusable hook for per-screen user preferences (filters, expanded sections,
 * list order/size, etc.). Any page adopts it incrementally by passing its own `screenCode`
 * (the same stable `code` already used in ROUTE_DEFINITIONS / LC_MenuItemOut) and its own
 * preferences shape.
 *
 * Passing `screenCode: ''` disables persistence altogether — the hook behaves as a plain
 * local `useState` seeded with `defaultValue`, with no network call and no Redux cache entry.
 * This lets generic hooks (e.g. `usePaginatedList`) call `useScreenPreferences` unconditionally
 * (required by the rules of hooks) even when the caller hasn't opted into a screen code yet.
 *
 * Read is cached in the `userPreferences` Redux slice for the current session (same store
 * lifetime as `authSlice`/`menuSlice`; cleared on logout/login): re-visiting a screen already
 * loaded this session reuses the cached value instead of hitting the backend again.
 * Save happens once when the user leaves the screen (unmount / screenCode change) instead of
 * on every edit — this keeps navigation between screens free of extra network calls, at the
 * cost of not persisting the very last change if the browser tab is closed/crashed instead of
 * navigated away from (acceptable: this is low-criticality UI preference data, not business data).
 */
export function useScreenPreferences<T extends Record<string, unknown>>(
  screenCode: string,
  defaultValue: T,
): UseScreenPreferencesResult<T> {
  const isEnabled = screenCode !== DISABLED_SCREEN_CODE;
  const dispatch = useAppDispatch();
  // `selectScreenPreferences` returns a new `createSelector` instance per call — memoised here
  // so every render (across all list hooks, even the ones without persistence) doesn't pay for
  // a fresh selector and a wasted `useSelector` resubscription.
  const selector = useMemo(() => selectScreenPreferences(screenCode), [screenCode]);
  const cached = useAppSelector(selector) as T | undefined;
  const [preferences, setPreferences] = useState<T>(cached ?? defaultValue);
  const [isLoaded, setIsLoaded] = useState(!isEnabled || cached !== undefined);
  const { createSignal } = useAbortController();
  // Last snapshot known to match the server (from cache/load, or from a completed save).
  // Compared on leave to skip saving when nothing actually changed.
  const lastKnownServerJsonRef = useRef<string | null>(
    cached ? serializePreferences(cached) : null,
  );
  // Mirrors `preferences` for the leave-effect's cleanup, which otherwise would close over a
  // stale value (the cleanup is only re-created when `screenCode` changes, not on every edit).
  const preferencesRef = useRef(preferences);

  useEffect(() => {
    preferencesRef.current = preferences;
  }, [preferences]);

  useEffect(() => {
    if (!isEnabled) {
      setIsLoaded(true);
      return;
    }

    if (cached !== undefined) {
      setPreferences(cached);
      lastKnownServerJsonRef.current = serializePreferences(cached);
      setIsLoaded(true);
      return;
    }

    let isMounted = true;
    setIsLoaded(false);

    const loadPreferences = async () => {
      const signal = createSignal();
      const response = await userPreferenceService.getScreenPreference(screenCode, signal);
      if (!isMounted || response.error?.code === 'ABORTED') return;

      const loaded = response.success && response.data
        ? parsePreferences(response.data.preferencesJson, defaultValue)
        : defaultValue;
      setPreferences(loaded);
      dispatch(setScreenPreferences({ screenCode, preferences: loaded }));
      lastKnownServerJsonRef.current = serializePreferences(loaded);
      setIsLoaded(true);
    };

    void loadPreferences();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenCode, isEnabled]);

  useEffect(() =>
    // Setup runs on mount/screenCode change but does nothing — only the cleanup matters here.
    // The cleanup fires when the screen is left (unmount) or when screenCode changes, which is
    // exactly the "onLeave" trigger this hook uses instead of an interaction-based debounce.
    () => {
      if (!isEnabled) return;
      if (lastKnownServerJsonRef.current === null) return;

      const current = preferencesRef.current;
      const json = serializePreferences(current);
      dispatch(setScreenPreferences({ screenCode, preferences: current }));
      if (json === lastKnownServerJsonRef.current) return;

      userPreferenceService.updateScreenPreference(screenCode, json).catch(() => {
        // eslint-disable-next-line no-console -- fallo silencioso intencional (baja criticidad)
        console.warn(`No se pudieron guardar las preferencias de la pantalla "${screenCode}"`);
      });
    }
  , [screenCode, dispatch, isEnabled]);

  const updatePreferences = useCallback((patch: Partial<T>) => {
    setPreferences(prev => ({ ...prev, ...patch }));
  }, []);

  return { preferences, updatePreferences, isLoaded };
}
